/** Durable web state — debounced write-through flush (#1110). */
import whitelistDoc from '../../../web_state_whitelist.json';
import { isConnectorAvailable } from './connector';

const WHITELIST = new Set<string>(whitelistDoc.keys);

const DEBOUNCE_MS = 400;
const pending = new Map<string, string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (!isConnectorAvailable()) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => { flushTimer = null; void flushPendingToDisk(); }, DEBOUNCE_MS);
}

async function flushPendingToDisk(): Promise<void> {
  if (!pending.size || !isConnectorAvailable()) { pending.clear(); return; }
  const entries = Object.fromEntries(pending);
  pending.clear();
  try {
    const tauri = (window as Window & { __TAURI__?: { core?: { invoke: (cmd: string, args?: unknown) => Promise<unknown> } } }).__TAURI__;
    if (!tauri?.core?.invoke) return;
    await tauri.core.invoke('set_web_state_entries', { entries });
  } catch {
    for (const [k, v] of Object.entries(entries)) pending.set(k, v);
  }
}

export function queueDurableWebStateWrite(key: string, value: string): void {
  if (!WHITELIST.has(key)) return;
  pending.set(key, value);
  scheduleFlush();
}

export function flushDurableWebStateBackfill(origGet: Storage['getItem'], keys: Iterable<string>): void {
  if (!isConnectorAvailable()) return;
  for (const key of keys) {
    try {
      const existing = origGet.call(localStorage, key);
      if (existing !== null) pending.set(key, existing);
    } catch { /* quota */ }
  }
  scheduleFlush();
}

export function resetDurableWebStateFlushForTests(): void {
  pending.clear();
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
}
